/**
 * Tier-0 local preview frame.
 *
 * At file-pick time we grab a single frame from the picked File entirely in the
 * browser — no byte leaves the machine — so the queue row and any placeholder
 * consumer show a real still of the video instantly, before the upload even
 * starts. Best-effort: any failure resolves to null and the caller falls back
 * to the plain thumbnail. The returned string is an object URL the caller must
 * revoke when the slot goes away (revokePreviewUrl).
 */

// Where in the clip to grab the frame. ~1s in avoids the black/fade-in first
// frame most exports open on, matching the server poster's default seek.
const PREVIEW_SEEK_SECONDS = 1;
// A capture that hasn't produced a frame by now is abandoned so a hostile file
// can never hold the pick handler open.
const CAPTURE_TIMEOUT_MS = 5000;

export function revokePreviewUrl(url: string | null): void {
  if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
}

// Resolve to an object URL for a captured JPEG frame, or null on any failure.
// Never rejects — Tier 0 must never block enqueue.
export function capturePreviewFrame(file: File): Promise<string | null> {
  if (typeof document === "undefined") return Promise.resolve(null);
  if (!file.type.startsWith("video/")) return Promise.resolve(null);

  return new Promise<string | null>((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute("src");
      video.load();
    };

    const done = (result: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(result);
    };

    const timer = setTimeout(() => done(null), CAPTURE_TIMEOUT_MS);

    video.muted = true;
    video.preload = "metadata";
    video.playsInline = true;

    video.addEventListener("error", () => done(null));

    video.addEventListener("loadedmetadata", () => {
      // Seek shy of EOF for a short clip so we never seek past the end.
      const target = Math.min(PREVIEW_SEEK_SECONDS, (video.duration || 0) / 2 || PREVIEW_SEEK_SECONDS);
      try {
        video.currentTime = target;
      } catch {
        done(null);
      }
    });

    video.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx || canvas.width === 0 || canvas.height === 0) return done(null);

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => done(blob ? URL.createObjectURL(blob) : null),
          "image/jpeg",
          0.7
        );
      } catch {
        done(null);
      }
    });

    video.src = objectUrl;
  });
}
